export type Impact = 'HEADS_UP' | 'SLOWER' | 'READ_ONLY' | 'FULL_OUTAGE';
export type Phase = 'upcoming' | 'notice' | 'soon' | 'live' | 'back' | 'over';

export type StatusWindow = {
  id: string;
  title: string;
  message: string;
  impact: Impact;
  products: string[];
  regions: string[];
  startsAt: string;
  endsAt: string | null;
  endedAt: string | null;
  noticeMinutes: number;
  latestUpdate: { at: string; message: string } | null;
  updatedAt: string;
};

export type StatusFile = {
  version: 1;
  generatedAt: string;
  productNames: Record<string, string>;
  windows: StatusWindow[];
};

export type Shown = {
  window: StatusWindow;
  phase: Phase;
  msToStart: number;
  msToEnd: number | null;
};

const MIN = 60000;
const SOON_MS = 15 * MIN;
const BACK_MS = 15 * MIN;
const IMPACTS = ['HEADS_UP', 'SLOWER', 'READ_ONLY', 'FULL_OUTAGE'];

function ms(iso: string | null): number {
  return iso === null ? NaN : Date.parse(iso);
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function optionalDate(v: unknown): string | null | undefined {
  if (v === null || v === undefined) return null;
  return typeof v === 'string' && !isNaN(Date.parse(v)) ? v : undefined;
}

function parseWindow(raw: unknown): StatusWindow | null {
  if (!isObject(raw)) return null;
  const { id, title, message, impact, startsAt, updatedAt } = raw;
  if (typeof id !== 'string' || typeof startsAt !== 'string' || isNaN(Date.parse(startsAt))) return null;
  if (typeof impact !== 'string' || IMPACTS.indexOf(impact) < 0) return null;
  const endsAt = optionalDate(raw.endsAt);
  const endedAt = optionalDate(raw.endedAt);
  if (endsAt === undefined || endedAt === undefined) return null;
  const update = raw.latestUpdate;
  return {
    id,
    title: typeof title === 'string' ? title : '',
    message: typeof message === 'string' ? message : '',
    impact: impact as Impact,
    products: strings(raw.products),
    regions: strings(raw.regions),
    startsAt,
    endsAt,
    endedAt,
    noticeMinutes: typeof raw.noticeMinutes === 'number' && raw.noticeMinutes > 0 ? raw.noticeMinutes : 0,
    latestUpdate:
      isObject(update) && typeof update.at === 'string' && typeof update.message === 'string'
        ? { at: update.at, message: update.message }
        : null,
    updatedAt: typeof updatedAt === 'string' ? updatedAt : '',
  };
}

export function parseStatus(raw: unknown): StatusFile | null {
  if (!isObject(raw) || raw.version !== 1 || !Array.isArray(raw.windows)) return null;
  const names: Record<string, string> = {};
  if (isObject(raw.productNames)) {
    Object.keys(raw.productNames).forEach((k) => {
      const v = (raw.productNames as Record<string, unknown>)[k];
      if (typeof v === 'string') names[k] = v;
    });
  }
  const windows: StatusWindow[] = [];
  raw.windows.forEach((w) => {
    const parsed = parseWindow(w);
    if (parsed) windows.push(parsed);
  });
  return {
    version: 1,
    generatedAt: typeof raw.generatedAt === 'string' ? raw.generatedAt : '',
    productNames: names,
    windows,
  };
}

function effectiveEndMs(w: StatusWindow): number {
  return ms(w.endedAt !== null ? w.endedAt : w.endsAt);
}

export function phaseOf(w: StatusWindow, nowMs: number): Phase {
  const start = ms(w.startsAt);
  if (nowMs < start) {
    if (nowMs >= start - SOON_MS) return 'soon';
    return nowMs < start - (w.noticeMinutes || 0) * MIN ? 'upcoming' : 'notice';
  }
  const end = effectiveEndMs(w);
  if (isNaN(end) || nowMs < end) return 'live';
  return nowMs < end + BACK_MS ? 'back' : 'over';
}

export function dismissKey(w: StatusWindow): string {
  return `${w.id}:${w.updatedAt}`;
}

function dismissible(s: Shown): boolean {
  if (s.phase === 'notice' || s.phase === 'back') return true;
  return s.phase === 'live' && (s.window.impact === 'HEADS_UP' || s.window.impact === 'SLOWER');
}

function rank(s: Shown): number {
  if (s.phase === 'live') {
    if (s.window.impact === 'FULL_OUTAGE') return 0;
    if (s.window.impact === 'READ_ONLY') return 1;
    return 3;
  }
  if (s.phase === 'soon') return 2;
  return s.phase === 'notice' ? 4 : 5;
}

export function visibleFor(
  file: StatusFile,
  product: string,
  region: string,
  nowMs: number,
  dismissed: Set<string>,
): Shown[] {
  const out: Shown[] = [];
  file.windows.forEach((w) => {
    if (product !== '*' && (w.products.indexOf(product) < 0 || w.regions.indexOf(region) < 0)) return;
    const phase = phaseOf(w, nowMs);
    if (phase === 'upcoming' || phase === 'over') return;
    const end = effectiveEndMs(w);
    const shown: Shown = {
      window: w,
      phase,
      msToStart: ms(w.startsAt) - nowMs,
      msToEnd: isNaN(end) ? null : end - nowMs,
    };
    if (dismissible(shown) && dismissed.has(dismissKey(w))) return;
    out.push(shown);
  });
  return out.sort((a, b) => rank(a) - rank(b) || ms(a.window.startsAt) - ms(b.window.startsAt));
}

export function clockOffsetMs(serverDateHeader: string | null, deviceNowMs: number): number {
  if (!serverDateHeader) return 0;
  const server = Date.parse(serverDateHeader);
  return isNaN(server) ? 0 : server - deviceNowMs;
}

export function regionCode(r: string | null): 'ASIA' | 'US' {
  return r !== null && r.toLowerCase() === 'us' ? 'US' : 'ASIA';
}
